import { Component, OnInit, OnDestroy, inject, input } from '@angular/core';

import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { ServiceTypeService } from '@fitness-force/api';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import type { ListServiceTypeResponse, ServiceTypeResponse } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { TranslateModule } from '@ngx-translate/core';
import { TextareaModule } from 'primeng/textarea';


@Component({
  selector: 'create-opportunity',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerModule,
    SelectModule,
    ToggleSwitchModule,
    MultiSelectModule,
    ErrorMsgComponent,
    TranslateModule,
    TextareaModule
],
  templateUrl: './create-opportunity.component.html'
})
export class CreateOpportunityComponent implements OnInit, OnDestroy {
  private serviceType = inject(ServiceTypeService);
  private store = inject(Store);

  readonly parentForm = input.required<FormGroup>();
  readonly tenantId = input.required<number>();
  interestedInCollection: any[] = [];
  private destroy$ = new Subject<void>();
  taskTypeCollection = CONSTANTS_DATA.TASKTYPE;
  taskOnCollection = [
    {
      name: 'Today',
      value: 'today',
      date: () => new Date()
    },
    {
      name: 'Tomorrow',
      value: 'tomorrow',
      date: () => new Date(new Date().setDate(new Date().getDate() + 1))
    },
    {
      name: 'Next Week',
      value: 'next_week',
      date: () => new Date(new Date().setTime(new Date().getTime() + 7 * 24 * 60 * 60 * 1000))
    },
    {
      name: 'Custom Date',
      value: 'custom date',
      date: () => new Date()
    }
  ];

  minDate: Date = new Date();
  currentTenantId: number = 0;

  ngOnInit() {
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: any) => {
        this.currentTenantId = tenant.selected_tenant.id;
        this.GetServiceType(this.currentTenantId);
      });

    this.setDefaultValues();

    // Setup validation subscriptions
    this.parentForm().get('createTask')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(createTask => {
        const controls = ['taskType', 'taskOn','notes'];
        controls.forEach(control => {
          const ctrl = this.parentForm().get(control);
          if (createTask) {
            ctrl?.enable();
            ctrl?.setValidators([Validators.required]);
          } else {
            ctrl?.disable();
            ctrl?.clearValidators();
          }
          ctrl?.updateValueAndValidity({ emitEvent: false });
        });
      });

    this.parentForm().get('createOpportunity')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(createOpportunity => {
        if (createOpportunity) {
          this.parentForm().get('interestedIn')?.enable();
          this.parentForm().get('interestedIn')?.setValidators([Validators.required]);
        } else {
          this.parentForm().get('interestedIn')?.disable();
          this.parentForm().get('interestedIn')?.clearValidators();
        }

      });

    // Handle custom date validation
    this.parentForm().get('taskOn')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(taskOn => {
        const taskOnDateControl = this.parentForm().get('taskOnDate');
        if (taskOn?.value === 'custom date') {
          taskOnDateControl?.enable();
          taskOnDateControl?.setValidators([Validators.required]);
        } else {
          taskOnDateControl?.disable();
          taskOnDateControl?.clearValidators();
        }
        taskOnDateControl?.updateValueAndValidity({ emitEvent: false });
      });
  }

  setDefaultValues(): void {
    this.parentForm().get('taskOn')?.setValue(this.taskOnCollection.find(e => e.name === 'Today'));
    this.parentForm().get('taskType')?.setValue(CONSTANTS_DATA.TASKTYPE.find(e => e.name === 'Call'));
    this.parentForm().get('taskOnDate')?.setValue(new Date());
  }

  GetServiceType(tenantId: number): void {
    this.serviceType.GetServiceType(tenantId).subscribe({
      next: (serviceTypes: ListServiceTypeResponse) => {
        if (serviceTypes.items) {
          serviceTypes.items.forEach((serviceType: ServiceTypeResponse) => {
            this.interestedInCollection.push({
              name: serviceType.name_primary_language,
              value: serviceType.id,
            });
          });
        }
      },
      error: (error) => {
        this.interestedInCollection = [];
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
} 